"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { YOUZU_API_BASE_URL } from "@/constants/api";
import { createAccessToken } from "@/services/token.service";

export type MatchLevel = "exact" | "similar" | "broad";

interface YouzuLensSDKProps {
  catalogueId?: string;
  lang?: string;
  matchLevel?: MatchLevel;
}

export function YouzuLensSDK({
  catalogueId,
  lang = "en",
  matchLevel = "similar",
}: YouzuLensSDKProps) {
  const lensRef = useRef<any>(null);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const lens = lensRef.current;
    if (!lens) return;
    lens.setAttribute("match-level", matchLevel);
  }, [matchLevel]);

  useEffect(() => {
    // Ensure the lens element is available
    const lens = lensRef.current;
    if (!lens) return;

    lens.resolveToken = async () => {
      console.log("🔄 Resolving token via proxy...");
      try {
        const token = await createAccessToken({ catalogueId });
        console.log("✅ Token resolved successfully");
        return token;
      } catch (error) {
        console.error("❌ Failed to resolve token:", error);
        return null;
      }
    };

    const handleSearchStart = () => {
      console.log("🔄 Search started...");
      setLoading(true);
      setError(null);
      setResults([]);
    };

    const handleSearchComplete = (e: any) => {
      console.log("✅ Visual search completed:", e.detail.results);
      setLoading(false);
      setResults(e.detail.results || []);
    };

    const handleSearchError = (e: any) => {
      console.error("❌ Visual search error:", e.detail.error);
      setLoading(false);
      setError(e.detail.error || "An error occurred");
    };

    lens.addEventListener("youzu-lens.search.start", handleSearchStart);
    lens.addEventListener("youzu-lens.search.complete", handleSearchComplete);
    lens.addEventListener("youzu-lens.search.error", handleSearchError);

    return () => {
      lens.removeEventListener("youzu-lens.search.start", handleSearchStart);
      lens.removeEventListener(
        "youzu-lens.search.complete",
        handleSearchComplete,
      );
      lens.removeEventListener("youzu-lens.search.error", handleSearchError);
    };
  }, [catalogueId]);

  return (
    <div className="flex flex-col gap-6">
      <div className="w-full flex justify-center">
        <Card className="w-full max-w-[600px] border-0 shadow-none">
          <CardContent className="p-0">
            <youzu-lens
              ref={lensRef}
              id="lens"
              catalogue-id={
                catalogueId || "570209bd-c9aa-49c5-a525-294568e40c9e"
              }
              api-base-url={YOUZU_API_BASE_URL}
              lang={lang}
              className="block w-full min-h-[200px]"
            ></youzu-lens>
          </CardContent>
        </Card>
      </div>

      <div className="w-full">
        <Card className="h-full">
          <CardHeader>
            <CardTitle>Results</CardTitle>
            <CardDescription>
              Visual search results will appear here
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div
              id="results-area"
              className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4"
            >
              {loading &&
                Array(6)
                  .fill(0)
                  .map((_, i) => (
                    <div
                      key={i}
                      className="animate-pulse border rounded-lg p-4 space-y-3"
                    >
                      <div className="h-40 bg-gray-200 rounded"></div>
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  ))}

              {!loading && error && (
                <div className="col-span-full p-4 text-red-500 bg-red-50 border border-red-200 rounded-md">
                  {error}
                </div>
              )}

              {!loading && !error && results.length === 0 && (
                <div className="col-span-full text-center py-10 text-muted-foreground">
                  No results found. Upload an image to start searching.
                </div>
              )}

              {!loading &&
                results.map((result: any, index: number) => (
                  <div
                    key={index}
                    className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="aspect-square relative overflow-hidden bg-gray-100">
                      <img
                        src={result.productImageUrl}
                        alt={result.productName}
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="p-3">
                      <div
                        className="font-medium truncate"
                        title={result.productName}
                      >
                        {result.productName}
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        {result.currency}{" "}
                        {typeof result.price === "number"
                          ? result.price.toFixed(2)
                          : result.price}
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
